import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import useMarketSectors from "hooks/trpc/useMarketSectors"
import { useRouter } from "next/router"
import { useCallback, useMemo, useState } from "react"
import { DashboardGridWithSearch } from "../components/DashboardGridWithSearch"
import { Featured } from "../components/Featured"
import { RecentlyAdded } from "../components/RecentlyAdded"
import { Tags } from "../components/Tags"
import { useDashboards } from "../hooks/useDashboards"

const findTags = (
  dashboardTags: Array<string>,
  tags: { id: string; name: string }[]
) => {
  return tags
    .filter((tag) => dashboardTags?.includes(tag.id))
    .map(({ name, id }) => ({
      name,
      id: id,
    }))
}

export function Discover({
  tag,
  initialSearch = "",
}: {
  tag: string
  initialSearch?: string
}) {
  const dashboards = useDashboards()
  const router = useRouter()
  const { data: { data: marketSectors } = { data: [] } } = useMarketSectors()

  const [search, setSearch] = useState(initialSearch)

  const onSetSearch = useCallback(
    (newSearch: string) => {
      setSearch(newSearch)
      const query: { category: string; search?: string } = { category: tag }
      if (newSearch) {
        query.search = newSearch
      }
      router.push({ pathname: "/discover", query }, undefined, {
        shallow: true,
      })
    },
    [router, tag]
  )

  const {
    dashboardsWithTags,
    recentlyAdded,
    featuredDashboard,
    availableTags,
  } = useMemo(() => {
    const dashboardsWithTags = dashboards.map((dashboard) => ({
      ...dashboard,
      tags: findTags(dashboard.tags, marketSectors),
    }))

    const recentlyAdded = [...dashboardsWithTags].slice(0, 4)

    const featuredDashboard =
      dashboardsWithTags.find((dashboard) => dashboard.featured) ||
      dashboardsWithTags[0]

    const availableTags = [{ id: "all", name: "All" }]

    dashboardsWithTags
      .map((dashboard) => dashboard.tags)
      .flat()
      .forEach(({ id, name }) => {
        if (!availableTags.find((t) => t.id === id)) {
          availableTags.push({ id, name })
        }
      })

    return {
      dashboardsWithTags: dashboardsWithTags.sort((a, b) => {
        if (a.updated_at < b.updated_at) {
          return 1
        }
        if (a.updated_at > b.updated_at) {
          return -1
        }
        return 0
      }),
      recentlyAdded,
      featuredDashboard,
      availableTags,
    }
  }, [dashboards, marketSectors])

  return (
    <div className="flex flex-col gap-6 px-3 md:px-8 w-full mx-auto max-w-[1500px] items-between">
      <div className="h-full my-10 md:my-16">
        <Heading level={1} className="mb-3 !text-4xl">
          Discover
        </Heading>
        <p className="mb-6 text-sm text-fg-secondary">
          Real-time onchain analytics dashboards curated by leading analysts in
          the crypto market
        </p>
        <div className="flex gap-5 items-stretch flex-col lg:flex-row">
          {featuredDashboard && <Featured dashboard={featuredDashboard} />}
          {recentlyAdded.length > 0 && (
            <RecentlyAdded dashboards={recentlyAdded} />
          )}
        </div>
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-stretch pt-10">
          <Tags tag={tag} tags={availableTags} search={search} />
          <DashboardGridWithSearch
            tag={tag}
            dashboards={dashboardsWithTags}
            search={search}
            setSearch={onSetSearch}
          />
        </div>
      </div>
    </div>
  )
}
